import React from "react";
import { Download, ExternalLink, FileText, Receipt, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../../../../shared/ui";
import { TransactionItem } from "../../types";
import { TransactionStatusBadge } from "../TransactionStatusBadge";

export interface TransactionDocumentPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  documentUrl?: string;
  documentType?: "invoice" | "supporting_doc" | "receipt" | "proof";
  transaction?: TransactionItem;
}

const isImageFile = (url?: string) => {
  if (!url) return false;
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(url);
};

export const TransactionDocumentPreviewModal: React.FC<
  TransactionDocumentPreviewModalProps
> = ({
  open,
  onOpenChange,
  title,
  documentUrl,
  documentType = "invoice",
  transaction,
}) => {
  const navigate = useNavigate();

  if (!open) return null;

  const handleDownload = () => {
    if (!documentUrl || documentUrl === "N/A") {
      toast.error("Document is not available for download");
      return;
    }

    const link = document.createElement("a");
    link.href = documentUrl;
    link.download = `${title.toLowerCase().replace(/\s+/g, "-")}`;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenNewTab = () => {
    if (!documentUrl || documentUrl === "N/A") {
      toast.error("Document is not available");
      return;
    }
    window.open(documentUrl, "_blank", "noopener,noreferrer");
  };

  const handleNavigateToReceipt = () => {
    onOpenChange(false);
    navigate("/receipt/home", { state: transaction });
  };

  const isImage = isImageFile(documentUrl);
  const isReceiptWithoutUrl =
    documentType === "receipt" && (!documentUrl || documentUrl === "N/A");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 animate-in fade-in-0"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="fixed inset-0"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />

      <div className="relative z-10 flex h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-[#E4E7F4] px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F4EBFF] text-[#6F00FF]">
              {documentType === "receipt" ? (
                <Receipt size={20} />
              ) : (
                <FileText size={20} />
              )}
            </div>
            <div>
              <h2 className="text-[16px] font-semibold text-[#101828]">
                {title}
              </h2>
              <span className="text-[12px] text-[#667085]">
                {isReceiptWithoutUrl
                  ? "Generated transaction receipt"
                  : isImage
                    ? "Image preview"
                    : "Document viewer"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {documentUrl && documentUrl !== "N/A" ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDownload}
                  className="h-9 gap-1.5 rounded-lg border-[#D0D5DD] px-3 text-[13px] font-medium text-[#344054] hover:bg-[#F9FAFB]"
                >
                  <Download size={14} />
                  <span>Download</span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleOpenNewTab}
                  className="h-9 gap-1.5 rounded-lg border-[#D0D5DD] px-3 text-[13px] font-medium text-[#344054] hover:bg-[#F9FAFB]"
                >
                  <ExternalLink size={14} />
                  <span>Open in tab</span>
                </Button>
              </>
            ) : isReceiptWithoutUrl ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleNavigateToReceipt}
                className="h-9 gap-1.5 rounded-lg border-[#D0D5DD] px-3 text-[13px] font-medium text-[#344054] hover:bg-[#F9FAFB]"
              >
                <ExternalLink size={14} />
                <span>Open Receipt Page</span>
              </Button>
            ) : null}

            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="ml-2 flex h-9 w-9 items-center justify-center rounded-lg text-[#667085] transition-colors hover:bg-[#F2F4F7] hover:text-[#101828]"
              aria-label="Close modal"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-auto bg-[#F7F8FD] p-6">
          {documentUrl && documentUrl !== "N/A" ? (
            isImage ? (
              <div className="flex min-h-full items-center justify-center rounded-xl bg-white p-4 shadow-sm">
                <img
                  src={documentUrl}
                  alt={title}
                  className="max-h-[75vh] max-w-full rounded-lg object-contain"
                />
              </div>
            ) : (
              <iframe
                title={title}
                src={documentUrl}
                className="h-full min-h-[72vh] w-full rounded-xl border border-[#E4E7F4] bg-white shadow-sm"
              />
            )
          ) : isReceiptWithoutUrl && transaction ? (
            <div className="mx-auto max-w-md rounded-2xl border border-[#E4E7F4] bg-white p-6 shadow-sm">
              <div className="flex flex-col items-center border-b border-[#F2F4F7] pb-6 text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#ECFDF3] text-[#12B76A]">
                  <Receipt size={24} />
                </div>
                <h3 className="text-[18px] font-bold text-[#101828]">
                  TradeVu Transaction Receipt
                </h3>
                <span className="text-[12px] text-[#667085]">
                  {transaction.TransactionType || "Transfer"}
                </span>
                <div className="mt-4 text-[26px] font-extrabold text-[#101828]">
                  {transaction.currency || "USD"}{" "}
                  {Math.abs(Number(transaction.amount) || 0).toLocaleString()}
                </div>
              </div>

              <div className="flex flex-col gap-3 py-5 text-[13px]">
                <div className="flex justify-between">
                  <span className="text-[#667085]">Reference</span>
                  <span className="font-mono font-medium text-[#101828]">
                    {transaction.TransactionReference ||
                      transaction.RequestReference ||
                      transaction._id}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#667085]">Status</span>
                  <TransactionStatusBadge status={transaction.status || "Completed"} />
                </div>
                <div className="flex justify-between">
                  <span className="text-[#667085]">Business / Customer</span>
                  <span className="font-medium text-[#101828]">
                    {typeof transaction.business?.name === "object" && transaction.business?.name !== null
                      ? (transaction.business.name as any).value || (transaction.business.name as any).name || "N/A"
                      : transaction.business?.name ||
                        transaction.customer?.name ||
                        transaction.accountName ||
                        "N/A"}
                  </span>
                </div>
                {transaction.accountNumber && (
                  <div className="flex justify-between">
                    <span className="text-[#667085]">Account Number</span>
                    <span className="font-medium text-[#101828]">
                      {transaction.accountNumber}
                    </span>
                  </div>
                )}
                {transaction.bankName && (
                  <div className="flex justify-between">
                    <span className="text-[#667085]">Bank</span>
                    <span className="font-medium text-[#101828]">
                      {transaction.bankName}
                    </span>
                  </div>
                )}
              </div>

              <Button
                type="button"
                onClick={handleNavigateToReceipt}
                className="mt-2 w-full rounded-xl bg-[#6F00FF] py-2.5 text-[13px] font-semibold text-white hover:bg-[#5800CC]"
              >
                View Full Printable Receipt
              </Button>
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F2F4F7] text-[#667085]">
                <FileText size={24} />
              </div>
              <p className="text-[14px] font-medium text-[#101828]">
                Document not available
              </p>
              <p className="text-[12px] text-[#667085]">
                This document has not been uploaded or is currently unavailable.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
